import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ClusterDto, ClustersService, SessionService } from 'src/api/v2';

import { useUser } from 'src/features/authorization';

import {
  B2C_INFO_KEY,
  CLUSTERS_KEY,
  CLUSTER_KEY,
  USER_KEY,
  USER_STATUS_HAS_ACCEPTED_TERMS,
  USER_STATUS_HAS_CLUSTERS,
  USER_STATUS_LOADING,
  USER_STATUS_REGISTERED,
  USER_STAUTS_LOGGED_IN,
} from 'src/constants/hook-keys';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';

export default function useClusters() {
  const { user, chosenClusterId, setHasUserClusters, isAadUser } = useUser();
  const siteimprove = useSiteimprove();

  const [sessionService] = useState(new SessionService());
  const [clustersService] = useState(new ClustersService());

  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery({
    queryKey: [CLUSTERS_KEY],
    queryFn: () => clustersService.getMyClusters(),
    enabled: !!user,
    onSuccess: (data) => {
      const found = data.find((x) => x.id === chosenClusterId);

      if (found) {
        setHasUserClusters(true);
        queryClient.setQueryData([CLUSTER_KEY], found);
        return;
      }

      if (data.length === 0) {
        setHasUserClusters(false);
      } else {
        setHasUserClusters(true);
      }

      if (data.length === 1 && !chosenClusterId) {
        sessionService.storeChosenCluster(data[0]!.id).finally(() => {
          queryClient.setQueryData([CLUSTER_KEY], data[0]);
        });
        if (!isAadUser) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.CLUSTER_CHOOSER.id,
            SITEIMPROVE_DECLARATIONS.CLUSTER_CHOOSER.actions.selected(data[0]!.id),
          );
        }
      }
    },
    onError: () => setHasUserClusters(false),
  });

  const storeChosenClusterMutation = useMutation({
    mutationFn: async (cluster: ClusterDto) => {
      return await sessionService.storeChosenCluster(cluster.id);
    },
    onSuccess(_, cluster) {
      const keepQueries = [
        USER_STAUTS_LOGGED_IN,
        USER_STATUS_LOADING,
        USER_STATUS_REGISTERED,
        USER_STATUS_HAS_CLUSTERS,
        USER_STATUS_HAS_ACCEPTED_TERMS,
        B2C_INFO_KEY,
        CLUSTER_KEY,
        CLUSTERS_KEY,
      ];

      queryClient.setQueryData([CLUSTER_KEY], cluster);

      const userData = queryClient.getQueriesData([USER_KEY]);
      queryClient.setQueryData([USER_KEY], { ...userData, chosenClusterId: cluster.id });

      queryClient.resetQueries({
        predicate: (query) => {
          return !keepQueries.includes(query.queryKey[0] as string);
        },
      });
    },
  });

  const deleteChosenClusterMutation = useMutation({
    mutationFn: () => {
      return sessionService.deleteChosenCluster();
    },
    onSuccess() {
      queryClient.setQueryData([USER_STATUS_LOADING], false);
    },
  });

  return {
    clusters: data || [],

    ...rest,

    storeChosenClusterMutation,
    deleteChosenClusterMutation,
  };
}
